const CACHE_PREFIX = 'kifaka_market_gateway_v2';
const REQUEST_TIMEOUT_MS = 12000;
const PUBLIC_BRVM_MAIN_API_URL = 'https://script.google.com/macros/s/AKfycbyipZs3WV_MKi9k6LY6s5lkm7goTGNDQfx1d6RdyKF8RgD862-VC-oaGXRLigsIqFKp/exec';
const PUBLIC_BRVM_SUMMARY_API_URL = 'https://script.google.com/macros/s/AKfycbyWOUUiSTHas-44QkCy5QGaodaK9COdAEnC7ZPWMWbi1e3HnK39RPxTUYYeRnkl0jhD/exec';

const POLICY = {
  universe: { ttl: 60 * 1000, stale: 10 * 60 * 1000 },
  summary: { ttl: 60 * 1000, stale: 10 * 60 * 1000 },
  snapshot: { ttl: 60 * 1000, stale: 10 * 60 * 1000 },
  prices: { ttl: 60 * 1000, stale: 10 * 60 * 1000 },
  history: { ttl: 10 * 60 * 1000, stale: 60 * 60 * 1000 },
  manifest: { ttl: 12 * 60 * 60 * 1000, stale: 24 * 60 * 60 * 1000 },
  sectorMap: { ttl: 12 * 60 * 60 * 1000, stale: 24 * 60 * 60 * 1000 },
  bundle: { ttl: 60 * 1000, stale: 10 * 60 * 1000 }
};

const metrics = {
  requests: 0,
  failures: 0,
  totalDurationMs: 0,
  cacheHits: 0,
  staleHits: 0,
  staleRecoveryHits: 0,
  byType: {}
};

const memoryCache = new Map();
const inflight = new Map();
const asArray = (value) => (Array.isArray(value) ? value : []);

function deepClone(value) {
  if (value === null || value === undefined) return value;
  try {
    if (typeof structuredClone === 'function') return structuredClone(value);
  } catch (_err) {
    // noop
  }
  return JSON.parse(JSON.stringify(value));
}

function nowIso() {
  return new Date().toISOString();
}

function toNumber(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const raw = String(value).trim();
  if (!raw) return null;

  const compact = raw
    .replace(/[\u00A0\u202F\s]/g, '')
    .replace(/[^\d,.\-+eE]/g, '');
  if (!compact) return null;

  let normalized = compact;
  const hasComma = normalized.includes(',');
  const hasDot = normalized.includes('.');
  if (hasComma && hasDot) {
    if (normalized.lastIndexOf(',') > normalized.lastIndexOf('.')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = normalized.replace(/,/g, '');
    }
  } else if (hasComma) {
    const commaCount = (normalized.match(/,/g) || []).length;
    normalized = commaCount > 1 ? normalized.replace(/,/g, '') : normalized.replace(',', '.');
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function sanitizeSymbol(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9._-]/g, '')
    .slice(0, 24);
}

const PRICE_RESERVED_KEYS = new Set([
  'ASOF',
  'STALE',
  'SOURCE',
  'GENERATEDAT',
  'CACHEAGEMS',
  'ROWS',
  'DATA',
  'PRICES',
  'ERROR',
  'MESSAGE'
]);

function isLikelyMarketSymbol(value) {
  const symbol = sanitizeSymbol(value);
  if (!symbol || PRICE_RESERVED_KEYS.has(symbol)) return false;
  return /^[A-Z][A-Z0-9._-]{2,11}$/.test(symbol);
}

function metricBucket(type) {
  if (!metrics.byType[type]) {
    metrics.byType[type] = {
      requests: 0,
      failures: 0,
      totalDurationMs: 0
    };
  }
  return metrics.byType[type];
}

function trackMetric(type, durationMs, ok) {
  metrics.requests += 1;
  metrics.totalDurationMs += durationMs;
  const bucket = metricBucket(type);
  bucket.requests += 1;
  bucket.totalDurationMs += durationMs;
  if (!ok) {
    metrics.failures += 1;
    bucket.failures += 1;
  }
}

function isLocalHostname(value) {
  const hostname = String(value || '').trim().toLowerCase();
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

function isEmbeddedLocalApp(location = {}) {
  const protocol = String(location.protocol || '').trim().toLowerCase();
  const origin = String(location.origin || '').trim().toLowerCase();
  const hostname = String(location.hostname || '').trim().toLowerCase();
  const port = String(location.port || '').trim();

  if (protocol === 'file:' || protocol === 'capacitor:') return true;
  if (hostname !== 'localhost' || port) return false;
  return origin === 'https://localhost' || origin === 'capacitor://localhost';
}

function buildExternalApiUrl(base, params = {}) {
  const url = new URL(base);
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    url.searchParams.set(key, String(value));
  });
  return url.toString();
}

function buildPublicApiCandidate(requestPath) {
  try {
    const parsed = new URL(requestPath, 'https://kifaka.local');
    switch (parsed.pathname) {
      case '/api/market-universe':
        return buildExternalApiUrl(PUBLIC_BRVM_MAIN_API_URL, { action: 'manifest' });
      case '/api/prices':
        return buildExternalApiUrl(PUBLIC_BRVM_SUMMARY_API_URL);
      case '/api/dashboard-map':
        return buildExternalApiUrl(PUBLIC_BRVM_MAIN_API_URL, { action: 'dashboard_map' });
      case '/api/history':
        return buildExternalApiUrl(PUBLIC_BRVM_MAIN_API_URL, {
          action: 'history',
          symbol: parsed.searchParams.get('symbol'),
          limit: parsed.searchParams.get('limit')
        });
      default:
        return '';
    }
  } catch (_error) {
    return '';
  }
}

function snippetFromText(value, maxLen = 96) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLen);
}

async function parseJsonResponse(response, candidate) {
  const rawText = await response.text();
  if (!String(rawText || '').trim()) return null;
  try {
    return JSON.parse(rawText);
  } catch (_error) {
    const snippet = snippetFromText(rawText);
    throw new Error(
      snippet
        ? `Non-JSON response from ${candidate}: ${snippet}`
        : `Non-JSON response from ${candidate}`
    );
  }
}

function buildApiRequestCandidates(path) {
  const requestPath = String(path || '').trim();
  if (!requestPath) return [];
  if (!requestPath.startsWith('/api/')) return [requestPath];

  const seen = new Set();
  const candidates = [];
  const push = (value) => {
    const normalized = String(value || '').trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    candidates.push(normalized);
  };

  if (typeof window === 'undefined') {
    push(requestPath);
    push(`http://127.0.0.1:8788${requestPath}`);
    push(`http://localhost:8788${requestPath}`);
    return candidates;
  }

  const location = window.location || {};
  const protocol = String(location.protocol || '').toLowerCase();
  const origin = String(location.origin || '').trim();
  const hostname = String(location.hostname || '').trim().toLowerCase();
  const port = String(location.port || '').trim();
  const publicCandidate = buildPublicApiCandidate(requestPath);

  if (isEmbeddedLocalApp(location)) {
    push(publicCandidate);
    if (/^https?:/i.test(origin)) push(new URL(requestPath, origin).toString());
    push(requestPath);
    return candidates;
  }

  push(requestPath);

  if (protocol === 'file:') {
    push(publicCandidate);
    push(`http://127.0.0.1:8788${requestPath}`);
    push(`http://localhost:8788${requestPath}`);
    return candidates;
  }

  if (/^https?:/i.test(origin)) push(new URL(requestPath, origin).toString());

  if (isLocalHostname(hostname) && port !== '8788') {
    push(`http://127.0.0.1:8788${requestPath}`);
    push(`http://localhost:8788${requestPath}`);
  }

  return candidates;
}

function cacheKey(kind, suffix = '') {
  return `${CACHE_PREFIX}:${kind}${suffix ? `:${suffix}` : ''}`;
}

function readStorage(key) {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_err) {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_err) {
    // noop
  }
}

function getRecord(key) {
  if (memoryCache.has(key)) return memoryCache.get(key);
  const fromStorage = readStorage(key);
  if (!fromStorage || typeof fromStorage !== 'object') return null;
  memoryCache.set(key, fromStorage);
  return fromStorage;
}

function setRecord(key, value) {
  const record = { at: Date.now(), value };
  memoryCache.set(key, record);
  writeStorage(key, record);
}

function removeRecord(key) {
  memoryCache.delete(key);
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(key);
  } catch (_err) {
    // noop
  }
}

function fromPolicy(record, policy) {
  if (!record || !record.value) return null;
  const age = Date.now() - Number(record.at || 0);
  if (!Number.isFinite(age) || age < 0) return null;
  if (age <= policy.ttl) return { freshness: 'fresh', value: deepClone(record.value), age };
  if (age <= policy.stale) return { freshness: 'stale', value: deepClone(record.value), age };
  return null;
}

function withEnvelope(payload, defaults = {}) {
  return {
    asOf: payload?.asOf || payload?.generatedAt || defaults.asOf || nowIso(),
    stale: Boolean(payload?.stale),
    source: payload?.source || defaults.source || 'kifaka-market-gateway',
    generatedAt: payload?.generatedAt || defaults.generatedAt || nowIso(),
    cacheAgeMs: Number.isFinite(payload?.cacheAgeMs) ? Number(payload.cacheAgeMs) : null,
    ...payload
  };
}

function normalizeSummary(payload) {
  const marketSummary = payload?.marketSummary || payload?.market_summary || {};
  const indices = payload?.indices || {};
  return withEnvelope({
    marketSummary: {
      transactionValue: marketSummary.transactionValue || marketSummary.transaction_value || marketSummary.valeur_transactions || 'N/A',
      marketCapActions: marketSummary.marketCapActions || marketSummary.market_cap_actions || marketSummary.capitalisation_actions || 'N/A',
      marketCapBonds: marketSummary.marketCapBonds || marketSummary.market_cap_bonds || marketSummary.capitalisation_obligations || 'N/A'
    },
    indices: {
      brvmComposite: {
        value: indices?.brvmComposite?.value || indices?.brvm_c?.value || payload?.brvm_composite?.value || 'N/A',
        change: indices?.brvmComposite?.change || indices?.brvm_c?.change || payload?.brvm_composite?.change || '0,00%'
      },
      brvm30: {
        value: indices?.brvm30?.value || indices?.brvm_30?.value || payload?.brvm_30?.value || 'N/A',
        change: indices?.brvm30?.change || indices?.brvm_30?.change || payload?.brvm_30?.change || '0,00%'
      },
      brvmPres: {
        value: indices?.brvmPres?.value || indices?.brvm_pres?.value || payload?.brvm_pres?.value || 'N/A',
        change: indices?.brvmPres?.change || indices?.brvm_pres?.change || payload?.brvm_pres?.change || '0,00%'
      }
    },
    topGainers: asArray(payload?.topGainers || payload?.top5_gains),
    topLosers: asArray(payload?.topLosers || payload?.top5_losses)
  }, {
    asOf: payload?.lastUpdate || payload?.updated_at || payload?.asOf || nowIso(),
    source: payload?.source || 'kifaka-edge-summary'
  });
}

function normalizePrices(payload) {
  const arrayRows = asArray(payload?.rows).length
    ? payload.rows
    : (asArray(payload?.data).length ? payload.data : asArray(payload));

  const mapCandidate = payload?.prices && typeof payload.prices === 'object'
    ? payload.prices
    : ((payload && typeof payload === 'object' && !Array.isArray(payload)) ? payload : null);

  const rowsFromArray = asArray(arrayRows)
    .map((entry) => ({
      symbol: sanitizeSymbol(entry?.symbol),
      name: String(entry?.name || entry?.symbol || '').trim(),
      lastPrice: toNumber(entry?.lastPrice ?? entry?.price ?? entry?.close)
    }))
    .filter((entry) => entry.symbol && Number.isFinite(entry.lastPrice));

  const rowsFromMap = [];
  if (mapCandidate && typeof mapCandidate === 'object') {
    for (const [rawSymbol, rawValue] of Object.entries(mapCandidate)) {
      if (!isLikelyMarketSymbol(rawSymbol)) continue;
      const symbol = sanitizeSymbol(rawSymbol);
      const lastPrice = toNumber(
        (rawValue && typeof rawValue === 'object')
          ? (rawValue.lastPrice ?? rawValue.price ?? rawValue.close ?? rawValue.value)
          : rawValue
      );
      if (!Number.isFinite(lastPrice)) continue;
      rowsFromMap.push({
        symbol,
        name: (rawValue && typeof rawValue === 'object')
          ? String(rawValue.name || rawValue.symbol || symbol).trim()
          : symbol,
        lastPrice
      });
    }
  }

  const mergedRowsMap = new Map();
  for (const row of [...rowsFromArray, ...rowsFromMap]) {
    if (!row.symbol) continue;
    const existing = mergedRowsMap.get(row.symbol);
    if (!existing) {
      mergedRowsMap.set(row.symbol, row);
      continue;
    }
    const fallbackName = existing.name === existing.symbol || !existing.name;
    if (fallbackName && row.name) {
      mergedRowsMap.set(row.symbol, { ...existing, name: row.name, lastPrice: row.lastPrice });
    }
  }
  const rows = Array.from(mergedRowsMap.values());

  return withEnvelope({
    rows
  }, {
    asOf: payload?.asOf || payload?.generatedAt || nowIso(),
    source: payload?.source || 'kifaka-edge-prices'
  });
}

function normalizeSectorMap(payload) {
  const entries = asArray(payload?.entries).map((entry) => ({
    symbol: sanitizeSymbol(entry?.symbol),
    sector: String(entry?.sector || entry?.secteur || entry?.secteurs || '').trim() || 'Non classé'
  })).filter((entry) => entry.symbol);

  return withEnvelope({ entries }, {
    asOf: payload?.asOf || nowIso(),
    source: payload?.source || 'kifaka-edge-dashboard-map'
  });
}

function normalizeHistory(payload, symbol = '', limit = 20) {
  const cleanSymbol = sanitizeSymbol(payload?.symbol || symbol);
  const rows = asArray(payload?.rows).length ? payload.rows : asArray(payload?.data);

  const normalizedRows = rows
    .map((row) => {
      const date = String(row?.date || row?.Date || row?.day || row?.jour || row?.time || '').trim();
      const open = toNumber(row?.open ?? row?.Open ?? row?.o ?? row?.O ?? row?.ouverture);
      const high = toNumber(row?.high ?? row?.High ?? row?.h ?? row?.H);
      const low = toNumber(row?.low ?? row?.Low ?? row?.l ?? row?.L);
      const close = toNumber(row?.close ?? row?.Close ?? row?.c ?? row?.C ?? row?.lastPrice ?? row?.price);
      const volume = toNumber(row?.volume ?? row?.Volume ?? row?.v ?? row?.V) || 0;
      if (!date || !Number.isFinite(close)) return null;

      const resolvedOpen = Number.isFinite(open) ? open : close;
      const bounds = [resolvedOpen, close, high, low].filter(Number.isFinite);
      const resolvedHigh = Number.isFinite(high) ? Math.max(high, ...bounds) : Math.max(...bounds);
      const resolvedLow = Number.isFinite(low) ? Math.min(low, ...bounds) : Math.min(...bounds);

      return {
        date,
        time: date,
        open: resolvedOpen,
        high: resolvedHigh,
        low: resolvedLow,
        close,
        volume
      };
    })
    .filter(Boolean)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));

  return withEnvelope({
    symbol: cleanSymbol,
    limit: Math.max(2, Number.parseInt(limit, 10) || 20),
    rows: normalizedRows
  }, {
    asOf: payload?.asOf || nowIso(),
    source: payload?.source || 'kifaka-edge-history'
  });
}

function normalizeSnapshot(payload, limit = 20) {
  const rows = asArray(payload?.rows).map((row) => ({
    symbol: sanitizeSymbol(row?.symbol),
    name: String(row?.name || row?.symbol || '').trim(),
    sector: String(row?.sector || row?.secteur || '').trim() || 'Non classé',
    price: toNumber(row?.price ?? row?.lastPrice ?? row?.close),
    lastPrice: toNumber(row?.lastPrice ?? row?.price ?? row?.close),
    sessionPct: toNumber(row?.sessionPct ?? row?.sessionChangePct),
    pct24h: toNumber(row?.pct24h ?? row?.change24h),
    pct7d: toNumber(row?.pct7d ?? row?.change7d),
    volume24hXof: toNumber(row?.volume24hXof ?? row?.volumeXof),
    lastVolume: toNumber(row?.lastVolume ?? row?.volume) || 0,
    sparkline: asArray(row?.sparkline).map((point) => toNumber(point)).filter(Number.isFinite).slice(-Math.max(2, limit)),
    historyLoaded: Boolean(row?.historyLoaded)
  })).filter((row) => row.symbol);

  return withEnvelope({
    rows,
    summary: payload?.summary ? normalizeSummary(payload.summary) : null
  }, {
    asOf: payload?.asOf || payload?.generatedAt || nowIso(),
    source: payload?.source || 'kifaka-edge-snapshot'
  });
}

function universeToManifest(universePayload) {
  const entries = asArray(universePayload?.rows).map((row) => ({
    symbol: sanitizeSymbol(row?.symbol),
    name: String(row?.name || row?.symbol || '').trim()
  })).filter((entry) => entry.symbol);

  return withEnvelope({
    entries,
    symbols: entries.map((entry) => entry.symbol)
  }, {
    asOf: universePayload?.asOf || nowIso(),
    source: universePayload?.source || 'kifaka-edge-universe'
  });
}

async function fetchJson(url, metricType = 'edge') {
  const startedAt = Date.now();
  let lastError = null;
  try {
    const candidates = buildApiRequestCandidates(url);
    for (const candidate of candidates) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const response = await fetch(candidate, {
          method: 'GET',
          cache: 'no-store',
          redirect: 'follow',
          signal: controller.signal
        });
        if (!response.ok) {
          lastError = new Error(`HTTP ${response.status}`);
          if (response.status === 404 && candidate !== candidates[candidates.length - 1]) {
            continue;
          }
          throw lastError;
        }
        const payload = await parseJsonResponse(response, candidate);
        trackMetric(metricType, Date.now() - startedAt, true);
        return payload;
      } catch (error) {
        lastError = error;
        const message = String(error?.message || '');
        if (message.startsWith('HTTP') || candidate === candidates[candidates.length - 1]) {
          throw error;
        }
      } finally {
        clearTimeout(timer);
      }
    }
    throw lastError || new Error('API request failed');
  } catch (error) {
    if (!String(error?.message || '').startsWith('HTTP')) {
      trackMetric(metricType, Date.now() - startedAt, false);
    }
    throw error;
  }
}

function normalizeUniverseRows(payload) {
  return asArray(payload?.rows).map((row) => ({
    symbol: sanitizeSymbol(row?.symbol),
    name: String(row?.name || row?.symbol || '').trim(),
    sector: String(row?.sector || row?.secteur || '').trim() || 'Non classé',
    price: toNumber(row?.price ?? row?.lastPrice ?? row?.close)
  })).filter((row) => row.symbol);
}

async function fetchPublicUniverseFallback() {
  const [manifestPayload, sectorPayload, latestPayload] = await Promise.all([
    fetchJson(buildExternalApiUrl(PUBLIC_BRVM_MAIN_API_URL, { action: 'manifest' }), 'edge:universe:manifest'),
    fetchJson(buildExternalApiUrl(PUBLIC_BRVM_MAIN_API_URL, { action: 'dashboard_map' }), 'edge:universe:sector'),
    fetchJson(buildExternalApiUrl(PUBLIC_BRVM_SUMMARY_API_URL), 'edge:universe:summary')
  ]);

  const manifestEntries = asArray(manifestPayload?.entries)
    .map((entry) => ({
      symbol: sanitizeSymbol(entry?.symbol || entry?.ticker || entry?.code),
      name: String(entry?.name || entry?.label || entry?.symbol || '').trim()
    }))
    .filter((entry) => entry.symbol);

  const sectorBySymbol = new Map(
    asArray(sectorPayload?.entries)
      .map((entry) => [
        sanitizeSymbol(entry?.symbol),
        String(entry?.sector || entry?.secteur || entry?.secteurs || '').trim() || 'Non classé'
      ])
      .filter(([symbol]) => symbol)
  );

  const latestBySymbol = new Map(
    asArray(latestPayload)
      .map((entry) => {
        const symbol = sanitizeSymbol(entry?.symbol);
        if (!symbol) return null;
        return [
          symbol,
          {
            name: String(entry?.name || entry?.symbol || '').trim(),
            price: toNumber(entry?.lastPrice ?? entry?.price ?? entry?.close)
          }
        ];
      })
      .filter(Boolean)
  );

  const symbols = manifestEntries.length
    ? manifestEntries.map((entry) => entry.symbol)
    : [...latestBySymbol.keys()];

  const namesBySymbol = new Map(
    manifestEntries.map((entry) => [entry.symbol, entry.name || entry.symbol])
  );

  return {
    asOf: nowIso(),
    rows: symbols.map((symbol) => {
      const latest = latestBySymbol.get(symbol) || {};
      return {
        symbol,
        name: namesBySymbol.get(symbol) || latest.name || symbol,
        sector: sectorBySymbol.get(symbol) || 'Non classé',
        price: Number.isFinite(latest.price) ? latest.price : null
      };
    }),
    stale: false,
    source: 'kifaka-public-universe',
    generatedAt: nowIso(),
    cacheAgeMs: 0
  };
}

function resolveWithCache(kind, key, loader) {
  const policy = POLICY[kind];
  const cached = fromPolicy(getRecord(key), policy);

  if (cached?.freshness === 'fresh') {
    metrics.cacheHits += 1;
    return Promise.resolve(cached.value);
  }

  if (cached?.freshness === 'stale') {
    metrics.staleHits += 1;
    if (!inflight.has(key)) {
      const refresh = Promise.resolve()
        .then(loader)
        .then((value) => {
          setRecord(key, value);
          return value;
        })
        .catch(() => null)
        .finally(() => inflight.delete(key));
      inflight.set(key, refresh);
    }
    return Promise.resolve({
      ...cached.value,
      stale: true,
      cacheAgeMs: Number.isFinite(cached.age) ? cached.age : null,
      source: cached.value?.source || `cache-stale:${kind}`
    });
  }

  if (inflight.has(key)) return inflight.get(key);

  const request = Promise.resolve()
    .then(loader)
    .then((value) => {
      setRecord(key, value);
      return deepClone(value);
    })
    .catch((error) => {
      const backup = getRecord(key);
      if (backup?.value) {
        metrics.staleRecoveryHits += 1;
        return {
          ...deepClone(backup.value),
          stale: true,
          source: `${backup.value?.source || 'cache'}:recovered`
        };
      }
      throw error;
    })
    .finally(() => inflight.delete(key));

  inflight.set(key, request);
  return request;
}

export function getUniverse() {
  const key = cacheKey('universe');
  return resolveWithCache('universe', key, async () => {
    let payload = null;
    let rows = [];
    try {
      payload = await fetchJson('/api/market-universe', 'edge:universe');
    } catch (_error) {
      payload = await fetchPublicUniverseFallback();
    }
    rows = normalizeUniverseRows(payload);
    if (!rows.length) {
      payload = await fetchPublicUniverseFallback();
      rows = normalizeUniverseRows(payload);
    }
    if (!rows.length) throw new Error('Universe payload empty');
    return withEnvelope({
      asOf: payload?.asOf || nowIso(),
      rows,
      stale: Boolean(payload?.stale),
      source: payload?.source || 'kifaka-edge-universe',
      generatedAt: payload?.generatedAt || nowIso(),
      cacheAgeMs: Number.isFinite(payload?.cacheAgeMs) ? Number(payload.cacheAgeMs) : 0
    });
  });
}

export function getSummary() {
  const key = cacheKey('summary');
  return resolveWithCache('summary', key, async () => {
    const payload = await fetchJson('/api/market-snapshot?limit=20', 'edge:summary');
    const normalized = normalizeSummary(payload?.summary || payload);
    if (!normalized?.indices) throw new Error('Summary payload empty');
    return normalized;
  });
}

export function getSnapshot(limit = 20) {
  const normalizedLimit = Math.max(2, Math.min(120, Number.parseInt(limit, 10) || 20));
  const key = cacheKey('snapshot', String(normalizedLimit));
  return resolveWithCache('snapshot', key, async () => {
    const payload = await fetchJson(`/api/market-snapshot?limit=${encodeURIComponent(normalizedLimit)}`, 'edge:snapshot');
    const normalized = normalizeSnapshot(payload, normalizedLimit);
    if (!normalized?.rows?.length) throw new Error('Snapshot payload empty');
    return normalized;
  });
}

export function getPrices() {
  const key = cacheKey('prices');
  return resolveWithCache('prices', key, async () => {
    const payload = await fetchJson('/api/prices', 'edge:prices');
    const normalized = normalizePrices(payload);
    if (!normalized?.rows?.length) throw new Error('Prices payload empty');
    return normalized;
  });
}

export function getManifest() {
  const key = cacheKey('manifest');
  return resolveWithCache('manifest', key, async () => {
    const universe = await getUniverse();
    return universeToManifest(universe);
  });
}

export function getSectorMap() {
  const key = cacheKey('sectorMap');
  return resolveWithCache('sectorMap', key, async () => {
    const payload = await fetchJson('/api/dashboard-map', 'edge:sectorMap');
    const normalized = normalizeSectorMap(payload);
    if (!normalized?.entries?.length) {
      const universe = await getUniverse();
      return normalizeSectorMap({
        entries: asArray(universe?.rows).map((row) => ({
          symbol: row.symbol,
          sector: row.sector
        }))
      });
    }
    return normalized;
  });
}

export function getHistory(symbol, limit = 20) {
  const cleanSymbol = sanitizeSymbol(symbol);
  const normalizedLimit = Math.max(2, Math.min(5000, Number.parseInt(limit, 10) || 20));
  if (!cleanSymbol) return Promise.reject(new Error('Symbol is required'));
  const key = cacheKey('history', `${cleanSymbol}:${normalizedLimit}`);
  return resolveWithCache('history', key, async () => {
    const payload = await fetchJson(
      `/api/history?symbol=${encodeURIComponent(cleanSymbol)}&limit=${encodeURIComponent(normalizedLimit)}`,
      'edge:history'
    );
    const normalized = normalizeHistory(payload, cleanSymbol, normalizedLimit);
    if (!normalized?.rows?.length) throw new Error('History payload empty');
    return normalized;
  });
}

export async function getBundle(options = {}) {
  const includeUniverse = Boolean(options?.universe);
  const includeSummary = Boolean(options?.summary);
  const includeSnapshot = Boolean(options?.snapshot);
  const snapshotLimit = Math.max(2, Number.parseInt(options?.snapshot?.limit ?? options?.limit, 10) || 20);
  const historyLimit = Math.max(2, Number.parseInt(options?.historyLimit, 10) || 200);
  const historySymbols = asArray(options?.symbolsForHistory).map((symbol) => sanitizeSymbol(symbol)).filter(Boolean);
  const key = cacheKey('bundle', `${snapshotLimit}:${historyLimit}:${historySymbols.join(',')}:${includeUniverse ? 1 : 0}:${includeSummary ? 1 : 0}:${includeSnapshot ? 1 : 0}`);

  return resolveWithCache('bundle', key, async () => {
    const params = new URLSearchParams();
    params.set('snapshotLimit', String(snapshotLimit));
    params.set('historyLimit', String(historyLimit));
    if (historySymbols.length) params.set('historySymbols', historySymbols.join(','));

    try {
      const payload = await fetchJson(`/api/market-bundle?${params.toString()}`, 'edge:bundle');
      return {
        asOf: payload?.asOf || nowIso(),
        universe: includeUniverse ? withEnvelope(payload?.universe || { rows: [] }) : null,
        summary: includeSummary ? normalizeSummary(payload?.summary || {}) : null,
        snapshot: includeSnapshot ? normalizeSnapshot(payload?.snapshot || {}, snapshotLimit) : null,
        historyBySymbol: historySymbols.length ? (payload?.historyBySymbol || {}) : {},
        errors: {}
      };
    } catch (_err) {
      const tasks = [];
      if (includeUniverse) tasks.push(['universe', getUniverse()]);
      if (includeSummary) tasks.push(['summary', getSummary()]);
      if (includeSnapshot) tasks.push(['snapshot', getSnapshot(snapshotLimit)]);
      if (historySymbols.length) {
        tasks.push(['history', Promise.all(
          historySymbols.map(async (symbol) => [symbol, await getHistory(symbol, historyLimit)])
        ).then((pairs) => Object.fromEntries(pairs))]);
      }
      const settled = await Promise.allSettled(tasks.map(([, promise]) => promise));
      const out = {
        asOf: nowIso(),
        universe: null,
        summary: null,
        snapshot: null,
        historyBySymbol: {},
        errors: {}
      };
      settled.forEach((result, index) => {
        const name = tasks[index][0];
        if (result.status === 'fulfilled') {
          if (name === 'history') out.historyBySymbol = result.value;
          else out[name] = result.value;
        } else {
          out.errors[name] = result.reason?.message || 'Unknown error';
        }
      });
      return out;
    }
  });
}

export function invalidate(keys) {
  const requested = Array.isArray(keys) ? keys : (keys ? [keys] : []);
  const normalized = requested.map((key) => String(key || '').trim()).filter(Boolean);
  if (!normalized.length) {
    memoryCache.clear();
    try {
      if (typeof localStorage !== 'undefined') {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i += 1) {
          const key = localStorage.key(i);
          if (key && key.startsWith(`${CACHE_PREFIX}:`)) keysToRemove.push(key);
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
      }
    } catch (_err) {
      // noop
    }
    return;
  }

  normalized.forEach((kind) => {
    const prefix = `${CACHE_PREFIX}:${kind}`;
    [...memoryCache.keys()].forEach((key) => {
      if (key.startsWith(prefix)) removeRecord(key);
    });
    try {
      if (typeof localStorage !== 'undefined') {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i += 1) {
          const key = localStorage.key(i);
          if (key && key.startsWith(prefix)) keysToRemove.push(key);
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
      }
    } catch (_err) {
      // noop
    }
  });
}

export function getMetrics() {
  return deepClone(metrics);
}

export function resetMetrics() {
  metrics.requests = 0;
  metrics.failures = 0;
  metrics.totalDurationMs = 0;
  metrics.cacheHits = 0;
  metrics.staleHits = 0;
  metrics.staleRecoveryHits = 0;
  metrics.byType = {};
}

export function getCachePolicy() {
  return deepClone(POLICY);
}

export const getApiMetrics = getMetrics;
export const resetApiMetrics = resetMetrics;
